'use client'

import { useState, useMemo } from 'react'
import type { HistoryEntry } from '@lens/types/claude'
import { useApi } from '@excellent/ui/api'
import { lens } from '@excellent/ui/api/contracts/lens'
import { Button } from "@excellent/ui"
import { Badge, Card } from "@excellent/ui"
import { Skeleton } from "@excellent/ui"
import { Alert, AlertDescription } from "@excellent/ui"
import { PageHeader, SearchInput } from "@excellent/ui"
import { AlertTriangle, ChevronLeft, ChevronRight } from "@excellent/ui/icons"
import { useSnapshot } from '@lens/lib/use-snapshot'
import { formatTimestamp } from '@lens/lib/decode'

function formatTime(ts: number) {
 return formatTimestamp(ts)
}

function projectName(p: string) {
 const parts = p.replace(/\\/g, '/').split('/')
 return parts[parts.length - 1] || p
}

const PAGE_SIZE = 50

export default function HistoryPage() {
 const { data, error, isLoading } = useApi(lens.history, { query: { limit: 2000 } })
 const { snapshot } = useSnapshot()
 const scopedProject = snapshot?.scopedProject ?? null
 const [search, setSearch] = useState('')
 const [page, setPage] = useState(1)

 const entries = useMemo(() => {
 const all = [...(data?.history ?? [])].reverse()
 if (!search) return all
 const q = search.toLowerCase()
 return all.filter(e =>
 e.display?.toLowerCase().includes(q) ||
 // Project matches add nothing when every entry is from the same project.
 (!scopedProject && e.project?.toLowerCase().includes(q))
 )
 }, [data, search, scopedProject])

 const totalPages = Math.ceil(entries.length / PAGE_SIZE)
 const pageEntries = entries.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

 function handleSearch(v: string) {
 setSearch(v)
 setPage(1)
 }

 return (
 <div className="flex flex-col">
 <div className="space-y-4">

 <PageHeader
 title="History"
 description={data ? `${entries.length.toLocaleString()} entries` : undefined}
 toolbar={
 data ? (
 <SearchInput
 value={search}
 onChange={e => handleSearch(e.target.value)}
 placeholder="Search commands…"
 />
 ) : undefined
 }
 />

 {error && (
 <Alert variant="destructive">
 <AlertTriangle className="h-4 w-4" />
 <AlertDescription>Error loading data: {String(error)}</AlertDescription>
 </Alert>
 )}

 {isLoading && (
 <div className="space-y-2">
 {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-lg" />)}
 </div>
 )}

 {data && (
 <>
 {pageEntries.length === 0 ? (
 <div className="text-center py-16 text-foreground type-body">
 {(data.history?.length ?? 0) === 0
 ? 'No history found in ~/.claude/history.jsonl'
 : 'No entries match your search.'}
 </div>
 ) : (
 <>
 <div className="space-y-1">
 {pageEntries.map((entry, i) => (
 <Card
 key={i}
 className="gap-0 px-4 py-3"
 >
 <p className="type-body text-foreground leading-body break-words">
 {entry.display || '—'}
 </p>
 <div className="flex flex-wrap items-center gap-2 mt-1.5">
 {entry.timestamp && (
 <span className="type-body text-foreground">
 {formatTime(entry.timestamp)}
 </span>
 )}
 {!scopedProject && entry.project && (
 <Badge variant="outline" className="type-body">
 {projectName(entry.project)}
 </Badge>
 )}
 </div>
 </Card>
 ))}
 </div>

 {totalPages > 1 && (
 <div className="flex items-center justify-center gap-2 pt-2">
 <Button
 variant="outline"
 size="sm"
 onClick={() => setPage(p => Math.max(1, p - 1))}
 disabled={page === 1}
 >
 <ChevronLeft className="w-4 h-4" />
 Prev
 </Button>
 <span className="type-body text-foreground tabular-nums">
 {page} / {totalPages}
 </span>
 <Button
 variant="outline"
 size="sm"
 onClick={() => setPage(p => Math.min(totalPages, p + 1))}
 disabled={page === totalPages}
 >
 Next
 <ChevronRight className="w-4 h-4" />
 </Button>
 </div>
 )}
 </>
 )}
 </>
 )}
 </div>
 </div>
 )
}
